import { groupBy } from './groupBy';
import { CourierTravelModeString } from '../../../types/bundleTypes';
import dayjs, { Dayjs } from 'dayjs';
import { flatten } from '../../../utils/array';
import { Order } from '../../../types/orderTypes';
import { CourierDeliveryZoneCountsByDate } from '../../../types/reportTypes';
import {
  ShiftCourierJson,
  ShiftCourierWithDateJson,
  EmptyShiftWithId,
  ExtraWorkConst,
  ShiftStatusType,
} from '../../../const/ShiftConst';
import { groupByWithFormatedDateProp } from './groupByWithFormatedDateProp';
import { CalcExtraByTypeSortCompensation } from './CalcExtraByTypeSortCompensation';
import { divideBySafely } from './divideBySafely';
import { returnNullIfObjectEmpty } from './returnNullIfObjectEmpty';

export interface OrdersPerDay {
  date: any;

  compensationForShift: number;
  nrOfOrders: number;
  compensationForNormalDeliveries: number;

  totalCompensation: number;
  productionCostPerDelivery: number;
  name: string;
  travelMode: any;

  shift: ShiftCourierJson;
  extraWorkCompensation: ExtraWorkCompensation;
}

export interface ExtraWorkCompensation {
  allExtraWorkCompensation: number;
  sortWorkCompensation: number;
  inboundWorkCompensation: number;
  backupWorkCompensation: number;
  otherWorkCompensation: number;
}

export interface PerDayInvoice {
  date: any;
  compensationForShift: number;
  nrOfOrders: number;
  compensationForNormalDeliveries: number;
  totalCompensation: number;
  productionCostPerDelivery: number;
  shift: ShiftCourierJson;
  extraWorkCompensation: ExtraWorkCompensation;
  deviationCompensation: number;
}

export interface OrdersByDateProdCost {
  date: string;
  nrOfCouriers: number;
  sumOrdersOnDate: number;
  sumCompensationOnDate: number;
  earningsPerCourier: number;
  productionCostPerDelivery: number;
  shiftCostPerDelivery: number;
  variableCostPerDelivery: number;
  extraWorkCompensationPerDelivery: number;
  extraWorkSortCompensationPerDelivery: number;
}

export function nrOfUniqueCourierIds(orders: Order[]): number {
  function filterDuplicatedCurierIds(orders: Order[]): Order[] {
    return orders.reduce((acc, current) => {
      const x = acc.find(item => {
        if (current.courierId === item.courierId) {
          return current;
        } else return null;
      });
      if (!x) {
        return acc.concat([current]);
      } else {
        return acc;
      }
    }, []);
  }

  return filterDuplicatedCurierIds(orders).length;
}

export function nrOfOrdersConsolidatedByDate(orders: Order[]): {
  nrOfOrders: number;
  nrOfConsolidatedOrders: number;
  prosentageConsolidated: number;
  date: Dayjs;
}[] {
  returnNullIfObjectEmpty(orders);

  const ordersGroupedByDate: any = groupByWithFormatedDateProp(
    orders,
    'deliveredAt',
    false
  );

  const ordersByDateVizFormated = ordersGroupedByDate.map((key, index) => {
    const nrOfOrders = ordersGroupedByDate[index][1].length;

    const ordersByCustomer =
      orders && groupBy(ordersGroupedByDate[index][1], 'recipientPhoneNumber');
    const ordersByCustomerArray = Object.entries(ordersByCustomer);

    const ordersByCustomerArrayFiltered = ordersByCustomerArray.filter(
      (obj: any) => obj[1].length > 1
    );

    return {
      nrOfOrders: nrOfOrders,
      nrOfConsolidatedOrders: ordersByCustomerArrayFiltered.length,
      prosentageConsolidated: nrOfOrders / ordersByCustomerArrayFiltered.length,
      date: ordersGroupedByDate[index][0],
    };
  });

  return ordersByDateVizFormated;
}

export function getTravelMode(travelMode: string): string {
  if (travelMode === undefined || travelMode === null) {
    console.warn(
      '##############courierUser travelMode undefined###############'
    );
    return CourierTravelModeString.CAR.toString();
  }

  return travelMode;
}

export function ShiftForDate(
  courierListBookedFetched: ShiftCourierWithDateJson[],
  courierDeliveryZoneCountsByDate: CourierDeliveryZoneCountsByDate
): ShiftCourierJson {
  if (courierListBookedFetched && courierListBookedFetched.length) {
    const bookedCourierOnDate = courierListBookedFetched.find(
      bookedCourier =>
        dayjs(bookedCourier.date).format('YYYY-MM-DD') ===
        courierDeliveryZoneCountsByDate.date
    );
    if (bookedCourierOnDate === undefined) {
      return EmptyShiftWithId;
    }
    const shiftOnDateByCourierId = bookedCourierOnDate?.courierDayBooked.filter(
      shift => shift.status === ShiftStatusType.BOOKED
    );

    if (shiftOnDateByCourierId?.length > 0) {
      return shiftOnDateByCourierId[0];
    } else {
      return EmptyShiftWithId;
    }
  } else {
    return EmptyShiftWithId;
  }
}

export function reduceOrdersPerDay(
  ordersPerDay: OrdersPerDay[]
): OrdersByDateProdCost[] {
  const ordersGroupedByDate = groupBy(ordersPerDay, 'date');
  const ordersGroupedByDateList: [string, OrdersPerDay[]][] =
    Object.entries(ordersGroupedByDate);

  const dataToShow = ordersGroupedByDateList.map(
    (obj: [string, OrdersPerDay[]], dateIndex: number) => {
      const date: string = obj[0];
      const courierOrdersOnDate: OrdersPerDay[] = obj[1];
      const nrOfCouriers: number = courierOrdersOnDate.filter(
        obj => obj.nrOfOrders > 0
      ).length;

      const sumOrdersOnDate: number =
        courierOrdersOnDate.length &&
        courierOrdersOnDate
          .map(obj => obj.nrOfOrders)
          .reduce(
            (ordersByCourierAccumlator, ordersByOneCourier) =>
              ordersByCourierAccumlator + ordersByOneCourier
          );
      const sumCompensationOnDate: number = courierOrdersOnDate
        .map(obj => obj.totalCompensation)
        .reduce(
          (ordersByCourierAccumlator, ordersByOneCourier) =>
            ordersByCourierAccumlator + ordersByOneCourier
        );

      const extraWorkCompensation: number = courierOrdersOnDate
        .map(obj => obj.extraWorkCompensation.allExtraWorkCompensation)
        .reduce((acc, cur) => acc + cur);

      const extraWorkSortCompensationList: number[] = courierOrdersOnDate
        .map(objHere => objHere.shift)
        .filter(obj => obj !== undefined)
        .map(obj => {
          return CalcExtraByTypeSortCompensation(
            obj.extraWork,
            ExtraWorkConst.SORT.typeOfExtraWork
          );
        });

      const extraWorkSortCompensation: number =
        extraWorkSortCompensationList.length > 0
          ? extraWorkSortCompensationList.reduce((acc, cur) => acc + cur)
          : 0;

      const variableCostDeliveriesOnDate = courierOrdersOnDate
        .map(obj => obj.compensationForNormalDeliveries)
        .reduce(
          (ordersByCourierAccumlator, ordersByOneCourier) =>
            ordersByCourierAccumlator + ordersByOneCourier
        );

      const shiftCostOnDate = courierOrdersOnDate
        .map(obj => obj.compensationForShift)
        .reduce(
          (ordersByCourierAccumlator, ordersByOneCourier) =>
            ordersByCourierAccumlator + ordersByOneCourier
        );

      const shiftCostPerDelivery = divideBySafely(
        shiftCostOnDate,
        sumOrdersOnDate
      );

      const variableCostPerDelivery = divideBySafely(
        variableCostDeliveriesOnDate,
        sumOrdersOnDate
      );

      const productionCostPerDelivery = divideBySafely(
        sumCompensationOnDate,
        sumOrdersOnDate
      );

      const earningsPerCourier = divideBySafely(
        sumCompensationOnDate,
        nrOfCouriers
      );

      const extraWorkCompensationPerDelivery = divideBySafely(
        extraWorkCompensation,
        sumOrdersOnDate
      );
      const extraWorkSortCompensationPerDelivery = divideBySafely(
        extraWorkSortCompensation,
        sumOrdersOnDate
      );

      return {
        date,
        nrOfCouriers,
        sumOrdersOnDate,
        sumCompensationOnDate,
        earningsPerCourier,
        productionCostPerDelivery,
        shiftCostPerDelivery,
        variableCostPerDelivery,
        extraWorkCompensationPerDelivery,
        extraWorkSortCompensationPerDelivery,
      };
    }
  );

  const sortedByTimeDataToShow = dataToShow.sort((a, b) => {
    return dayjs(a.date).diff(dayjs(b.date));
  });

  return flatten(sortedByTimeDataToShow);
}
